import { useEffect, useRef } from 'react';
import type { SecurityPolicy } from '../lib/auth/security-policy';
import { useOnlineStatus } from './useOnlineStatus';

const CHECK_INTERVAL_MS = 60_000; // 1 minute

/**
 * Periodically checks whether the current session has exceeded the allowed
 * offline duration or overall session timeout, and calls `onExpired` when it has.
 *
 * Policies are platform-managed — zoo admins cannot weaken them.
 */
export function useSessionTimeout(
  policy: SecurityPolicy,
  sessionCreatedAt: string | null,
  onExpired: () => void,
): void {
  const { isOnline, lastOnlineAt } = useOnlineStatus();
  const onExpiredRef = useRef(onExpired);
  onExpiredRef.current = onExpired;

  useEffect(() => {
    if (!sessionCreatedAt) return;

    function check() {
      const now = Date.now();

      // 1. Overall session timeout
      const sessionStart = new Date(sessionCreatedAt!).getTime();
      const sessionMaxMs = policy.sessionTimeoutHours * 3600_000;
      if (now - sessionStart > sessionMaxMs) {
        console.warn('[SessionTimeout] Session exceeded max lifetime, forcing re-auth');
        onExpiredRef.current();
        return;
      }

      // 2. Max offline duration (only check when actually offline)
      if (!isOnline && lastOnlineAt) {
        const offlineMs = now - lastOnlineAt.getTime();
        const maxOfflineMs = policy.maxOfflineHours * 3600_000;
        if (offlineMs > maxOfflineMs) {
          console.warn('[SessionTimeout] Exceeded max offline duration, forcing re-auth');
          onExpiredRef.current();
          return;
        }
      }
    }

    // Run immediately on mount / policy change
    check();

    const interval = setInterval(check, CHECK_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [policy.sessionTimeoutHours, policy.maxOfflineHours, sessionCreatedAt, isOnline, lastOnlineAt]);
}
